/**
 * UX safety net, not protocol logic: after joining a room with zero video tracks, wait
 * a while and — if still nothing — tell the host so it can show the operator something
 * more useful than a frozen black screen. Nothing here is specific to WebRTC signaling;
 * it only reads `pipeline.countVideoTracks()`.
 *
 * A single instance is created once per session (by the controller in
 * `operator-session.ts`) and handed to `connect-phase.ts`/`teardown-phase.ts` as
 * `ctx.videoWatchdog` — the pending timer is this factory's own private state, never a
 * field on any shared object.
 */

import { emitStatus, type EmitStatusOpts } from "./session-status";
import { STATUS_CODES } from "../status-codes";

/**
 * How long to wait, after a successful join with zero video tracks, before treating
 * "no video producer showed up" as worth surfacing to the user rather than staying
 * silent forever. Generous enough that a streamer booting its camera pipeline just
 * after the operator joins is not mistaken for an empty room, but short enough that a
 * genuinely producer-less room (nobody ever joined) does not read as a silent hang.
 * Reversible: the resulting status clears itself the moment a video consumer attaches.
 */
const VIDEO_PRODUCER_TIMEOUT_MS = 15_000;

/** The one thing this file needs from the video pipeline — see the header doc above. */
export interface VideoTrackCounter {
  countVideoTracks: () => number;
}

export interface CreateVideoProducerWatchdogDeps {
  pipeline: VideoTrackCounter;
  appendDiag: (s: string, opts?: { level?: string }) => void;
  opts: EmitStatusOpts;
  isClosed: () => boolean;
}

export interface VideoProducerWatchdog {
  scheduleCheck: () => void;
  clear: () => void;
}

export function createVideoProducerWatchdog({
  pipeline,
  appendDiag,
  opts,
  isClosed,
}: CreateVideoProducerWatchdogDeps): VideoProducerWatchdog {
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clear() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  /**
   * Joined with zero video tracks — normal for a moment right after join (the streamer
   * may still be booting its camera pipeline), but silent forever otherwise leaves the
   * operator staring at an unchanging screen with no indication anything is wrong (e.g.
   * an empty room — no producer ever connected). Surfaces a status once via `emitStatus`
   * rather than forcing a session failure: the check re-verifies the track count itself,
   * so a video consumer that attaches before the timer fires (or after, via a later
   * VIDEO_SLOT_ACTIVE) naturally supersedes it — nothing here permanently blocks recovery.
   */
  function scheduleCheck() {
    clear();
    timer = setTimeout(() => {
      timer = null;
      if (isClosed() || pipeline.countVideoTracks() > 0) return;
      appendDiag(
        "После join нет video consumer (newConsumer). Проверьте: стример запущен до оператора; docker logs mediasoup | grep newConsumer; перезапуск комнаты (docker compose down).",
        { level: "warn" }
      );
      emitStatus(opts, STATUS_CODES.NO_VIDEO_PRODUCER);
    }, VIDEO_PRODUCER_TIMEOUT_MS);
  }

  return { scheduleCheck, clear };
}
